/**
 * Audio utility functions extracted from audio-visualizer.ts
 * These are pure functions with no side effects
 */

/**
 * Audio visualizer configuration constants
 */
export const AUDIO_CONSTANTS = {
  SPEECH_FREQUENCY_RATIO: 0.4,
  FFT_SIZE: 256,
  SMOOTHING_TIME_CONSTANT: 0.8,
  MIN_DECIBELS: -90,
  MAX_DECIBELS: -10,
} as const;

/**
 * Get audio frequency data from analyser
 * Optimized for human speech frequencies (85Hz - 4kHz)
 */
export function getAudioFrequencyData(analyser: AnalyserNode, pointsPerSide: number): number[] {
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);
  analyser.getByteFrequencyData(dataArray);

  const speechBinCount = Math.floor(bufferLength * AUDIO_CONSTANTS.SPEECH_FREQUENCY_RATIO);
  const result: number[] = [];

  // Ensure step is at least 1 to avoid division by zero
  const step = Math.max(1, Math.floor(speechBinCount / pointsPerSide));

  for (let i = 0; i < pointsPerSide; i++) {
    let sum = 0;
    const binIndex = i * step;
    // Sample within available speech bins
    for (let j = 0; j < step && binIndex + j < speechBinCount; j++) {
      sum += dataArray[binIndex + j] || 0;
    }
    const normalizedValue = sum / step / 255;
    result.push(normalizedValue);
  }

  return result;
}

/**
 * Combine audio data from multiple sources
 * Takes the maximum value at each frequency band
 */
export function combineAudioData(dataArrays: number[][], pointsPerSide: number): number[] {
  if (dataArrays.length === 0) {
    return new Array(pointsPerSide).fill(0);
  }

  if (dataArrays.length === 1) {
    return dataArrays[0];
  }

  const result: number[] = [];
  for (let i = 0; i < pointsPerSide; i++) {
    const max = Math.max(...dataArrays.map((data) => data[i] || 0));
    result.push(max);
  }

  return result;
}

/**
 * Color utility functions
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}

export function interpolateColor(color1: string, color2: string, factor: number): string {
  const c1 = hexToRgb(color1);
  const c2 = hexToRgb(color2);

  const r = Math.round(c1.r + (c2.r - c1.r) * factor);
  const g = Math.round(c1.g + (c2.g - c1.g) * factor);
  const b = Math.round(c1.b + (c2.b - c1.b) * factor);

  return `rgb(${r}, ${g}, ${b})`;
}

export function getBarColor(
  intensity: number,
  colorLow: string,
  colorBase: string,
  colorHigh: string
): string {
  const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;

  if (prefersHighContrast) {
    return intensity > 0.5 ? '#000000' : '#666666';
  }

  if (intensity < 0.5) {
    return interpolateColor(colorLow, colorBase, intensity * 2);
  } else {
    return interpolateColor(colorBase, colorHigh, (intensity - 0.5) * 2);
  }
}
